import { bool, string } from "prop-types";
import {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import styled from "styled-components";
import { opacity } from "theme";
import { colorKeys, themeSizes } from "theme/constants";

import { AnimatePresence, motion, useAnimationFrame } from "framer-motion";

import FnrContainedIcon from "UI/FnrContainedIcon";
import { inOutZoom } from "UI/motionStyles";

import setLeftTopFromCenter from "Utilities/elementFunctions/setLeftTopFromCenter";

const zIndexDelay = 0.1;
const fnrIconBubbleSize = 140;
const SDiv = styled(motion.div)`
  position: absolute;
  width: 112px;
  height: 112px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  & > div {
    position: relative;
    top: ${({ $childTop }) => $childTop || 0}px;
    left: ${({ $childLeft }) => $childLeft || 0}px;
  }
`;

const Container = styled("div")`
  width: ${fnrIconBubbleSize}px;
  height: ${fnrIconBubbleSize}px;
  position: relative;
  ${opacity}
  transition: opacity 300ms;
`;

const radialStart = Math.PI * 1.5;
const radialIncrement = Math.PI * (2 / 3);
const radius = 32; // px
const xCorrection = 22; // px
const yCorrection = -4; //px
const speed = Math.PI * (1 / 2); // radians per second

const idxProps = [
  {
    container: {
      $zIndex: 1,
    },
    icon: {
      color: colorKeys.grey,
      backgroundColor: colorKeys.brandFnBlue,
      size: themeSizes.decorative3,
    },
  },
  {
    container: {
      $zIndex: 0,
      $childTop: 4,
      $childLeft: -12,
    },
    icon: {
      color: colorKeys.lightBlue,
      backgroundColor: colorKeys.lightBlue,
      size: themeSizes.decorative2,
    },
  },
  {
    container: {
      $zIndex: 2,
      $childTop: -12,
      $childLeft: -4,
    },
    icon: {
      color: colorKeys.blue,
      backgroundColor: colorKeys.blue,
      size: themeSizes.decorative1,
    },
  },
];

/**
 *
 * @param {object} param0
 * @param {string} param0.icon - the icon the should display. Change what is passed in and create the animation effect
 * @param {boolean} param.transparent - Setting to true will dim the display so things are easily seen in front of it.
 * @returns
 */
const FnrIconBubbles = ({ icon, transparent }) => {
  const [iconHistory, setIconHistory] = useState([icon, icon, icon]);
  const [initAnimation, setInitAnimation] = useState(true);
  const containerRef = useRef();
  const refs = useRef([createRef(), createRef(), createRef()]);
  const timer = useRef(Date.now());
  const currentRadians = useRef(radialStart);
  const targetRadians = useMemo(
    () => radialStart + (iconHistory.length - 3) * radialIncrement,
    [iconHistory.length]
  );
  const shiftIcon = useCallback(
    (newIcon) => {
      setIconHistory([newIcon, ...iconHistory]);
    },
    [iconHistory]
  );

  const idxOffset1 = (iconHistory.length + 1) % 3;
  const idxOffset2 = iconHistory.length % 3;
  const idxOffset3 = (iconHistory.length - 1) % 3;
  const radianOffsets = [radialIncrement, 0, -radialIncrement];

  useAnimationFrame(() => {
    const changeAmount = ((Date.now() - timer.current) / 1000) * speed;
    timer.current = Date.now();
    if (!containerRef.current) return;
    if (currentRadians.current >= targetRadians && !initAnimation) return;

    // Only do this expection the first time
    if (initAnimation) setInitAnimation(false);

    const containerDimensions = [
      containerRef.current.clientWidth,
      containerRef.current.clientHeight,
    ];

    refs.current.forEach((elemRef, idx) => {
      const { current: elem } = elemRef;
      if (!elem) return;
      if (!initAnimation) currentRadians.current += changeAmount;
      const newCenter = [
        radius * Math.sin(currentRadians.current + radianOffsets[idx]) +
          containerDimensions[0] / 2 +
          xCorrection,
        radius * Math.cos(currentRadians.current + radianOffsets[idx]) +
          containerDimensions[1] / 2 +
          yCorrection,
      ];

      setLeftTopFromCenter(elem, newCenter);
    });
  });

  useEffect(() => {
    if (iconHistory[0] !== icon) {
      shiftIcon(icon);
    }
  }, [icon, iconHistory, shiftIcon]);

  return (
    <AnimatePresence>
      {icon && (
        <motion.div {...inOutZoom}>
          <Container
            ref={containerRef}
            data-cy="FnrIconBubbles_Container"
            $opacity={transparent ? ".3" : "1"}
          >
            <SDiv
              ref={refs.current[0]}
              {...idxProps[idxOffset1].container}
              animate={{
                zIndex: idxProps[idxOffset1].container.$zIndex,
                transition: { delay: zIndexDelay },
              }}
            >
              <FnrContainedIcon
                {...idxProps[idxOffset1].icon}
                icon={iconHistory[idxOffset1]}
              />
            </SDiv>
            <SDiv
              ref={refs.current[1]}
              {...idxProps[idxOffset2].container}
              animate={{
                zIndex: idxProps[idxOffset2].container.$zIndex,
                transition: { delay: zIndexDelay },
              }}
            >
              <FnrContainedIcon
                {...idxProps[idxOffset2].icon}
                icon={iconHistory[idxOffset2]}
              />
            </SDiv>
            <SDiv
              ref={refs.current[2]}
              {...idxProps[idxOffset3].container}
              animate={{
                zIndex: idxProps[idxOffset3].container.$zIndex,
                transition: { delay: zIndexDelay },
              }}
            >
              <FnrContainedIcon
                {...idxProps[idxOffset3].icon}
                icon={iconHistory[idxOffset3]}
              />
            </SDiv>
          </Container>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

FnrIconBubbles.propTypes = { icon: string, transparent: bool };

export { fnrIconBubbleSize };
export default FnrIconBubbles;
